// frontend/src/components/dashboard/PollManager.jsx
import React, { useState, useEffect } from 'react';
import { PlusIcon, ChatBubbleLeftRightIcon, CheckCircleIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import groupDashboardService from '../../services/groupDashboardService';
import Button from '../common/Button';

const PollManager = ({ groupId, membership, user }) => {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  const [newPoll, setNewPoll] = useState({
    question: '',
    options: ['', ''],
    expiresAt: ''
  });
  
  useEffect(() => {
    loadPolls();
  }, [groupId]);
  
  const loadPolls = async () => {
    try {
      setLoading(true);
      const pollsData = await groupDashboardService.getPolls(groupId);
      setPolls(pollsData);
    } catch (error) {
      console.error('Failed to load polls:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreatePoll = async (e) => {
    e.preventDefault();
    try {
      const pollData = {
        question: newPoll.question,
        options: newPoll.options.filter(opt => opt.trim() !== ''),
        expiresAt: newPoll.expiresAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      };
      
      await groupDashboardService.createPoll(groupId, pollData);
      
      setNewPoll({
        question: '',
        options: ['', ''],
        expiresAt: ''
      });
      setShowCreateForm(false);
      loadPolls();
    } catch (error) {
      console.error('Failed to create poll:', error);
    }
  };
  
  const handleVote = async (pollId, optionIndex) => {
    try {
      await groupDashboardService.voteOnPoll(groupId, pollId, optionIndex);
      loadPolls();
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };
  
  const addOption = () => {
    setNewPoll({
      ...newPoll,
      options: [...newPoll.options, '']
    });
  };
  
  const updateOption = (index, value) => {
    const newOptions = [...newPoll.options];
    newOptions[index] = value;
    setNewPoll({ ...newPoll, options: newOptions });
  };
  
  const removeOption = (index) => {
    const newOptions = newPoll.options.filter((_, i) => i !== index);
    setNewPoll({ ...newPoll, options: newOptions });
  };
  
  const getTotalVotes = (poll) => {
    return poll.options.reduce((sum, option) => sum + option.votes, 0);
  };
  
  if (loading) {
    return <div className="text-center py-8">Loading polls...</div>;
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Polls & Voting</h2>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-4 py-2 rounded-lg"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Poll
        </Button>
      </div>
      
      {/* Create Poll Form */}
      {showCreateForm && (
        <div className="mb-6 p-6 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl border border-yellow-100">
          <h3 className="font-bold text-gray-800 mb-4 text-lg">Create New Poll</h3>
          
          <form onSubmit={handleCreatePoll} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Poll Question
              </label>
              <input
                type="text"
                value={newPoll.question}
                onChange={(e) => setNewPoll({ ...newPoll, question: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                placeholder="e.g., Where should we eat tonight?"
                required
              />
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Options (at least 2)
                </label>
                <button
                  type="button"
                  onClick={addOption}
                  className="text-sm text-yellow-600 hover:text-yellow-800"
                >
                  + Add Option
                </button>
              </div>
              
              {newPoll.options.map((option, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    placeholder={`Option ${index + 1}`}
                    required
                  />
                  {newPoll.options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiry Date (Optional)
              </label>
              <input
                type="datetime-local"
                value={newPoll.expiresAt}
                onChange={(e) => setNewPoll({ ...newPoll, expiresAt: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-6 py-2 rounded-lg"
              >
                Create Poll
              </Button>
            </div>
          </form>
        </div>
      )}
      
      {/* Polls List */}
      <div className="space-y-6">
        {polls.length === 0 ? (
          <div className="text-center py-12">
            <ChatBubbleLeftRightIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Polls Yet</h3>
            <p className="text-gray-600 mb-6">Create a poll to get group opinions!</p>
            <Button
              onClick={() => setShowCreateForm(true)}
              className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-6 py-3 rounded-lg"
            >
              Create First Poll
            </Button>
          </div>
        ) : (
          polls.map((poll) => {
            const totalVotes = getTotalVotes(poll);
            const hasVoted = poll.voters?.some(v => v.userId.toString() === user._id);
            const isExpired = new Date(poll.expiresAt) < new Date();
            
            return (
              <div key={poll._id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{poll.question}</h3>
                    <div className="flex items-center space-x-4 mt-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <UserGroupIcon className="h-4 w-4 mr-1" />
                        {totalVotes} votes
                      </div>
                      <div className="text-sm text-gray-600">
                        Expires: {new Date(poll.expiresAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  
                  {hasVoted && (
                    <div className="flex items-center text-green-600">
                      <CheckCircleIcon className="h-5 w-5 mr-1" />
                      <span className="text-sm font-medium">Voted</span>
                    </div>
                  )}
                </div>
                
                <div className="space-y-3">
                  {poll.options.map((option, index) => {
                    const percentage = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0;
                    const isVotedOption = hasVoted && poll.voters?.find(v => 
                      v.userId.toString() === user._id && v.optionIndex === index
                    );
                    
                    return (
                      <div key={index} className="space-y-2">
                        <div className="flex justify-between">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => !hasVoted && !isExpired && handleVote(poll._id, index)}
                              disabled={hasVoted || isExpired}
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                isVotedOption
                                  ? 'border-green-500 bg-green-100'
                                  : hasVoted
                                  ? 'border-gray-300'
                                  : 'border-gray-300 hover:border-yellow-500 cursor-pointer'
                              } ${isExpired ? 'cursor-not-allowed' : ''}`}
                            >
                              {isVotedOption && (
                                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                              )}
                            </button>
                            <span className={`font-medium ${
                              isVotedOption ? 'text-green-700' : 'text-gray-700'
                            }`}>
                              {option.text}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {option.votes} votes ({percentage}%)
                          </div>
                        </div>
                        
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100">
                  <div className="flex items-center">
                    <div className="h-8 w-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-2">
                      {poll.createdBy?.name?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">{poll.createdBy?.name}</p>
                      <p className="text-xs text-gray-500">
                        Created {new Date(poll.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  {isExpired && (
                    <div className="text-sm text-red-600 font-medium">
                      Poll Expired
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PollManager;